import { client } from "../../amplify-client";

function normalizeString(value) {
  return String(value ?? "").trim();
}

function maxSk(left, right) {
  const l = normalizeString(left);
  const r = normalizeString(right);
  return l > r ? l : r;
}

function hasConversationReadModel() {
  const model = client?.models?.ConversationRead;
  return Boolean(
    model &&
      typeof model.list === "function" &&
      typeof model.get === "function" &&
      typeof model.create === "function" &&
      typeof model.update === "function"
  );
}

function hasInboxProjectionModel() {
  const model = client?.models?.UserInboxItem;
  return Boolean(
    model &&
      typeof model.get === "function" &&
      typeof model.update === "function"
  );
}

async function resetInboxProjectionUnreadCount({
  userId,
  chatPk,
  viewedAtIso,
}) {
  if (!hasInboxProjectionModel()) return false;

  try {
    const getResponse = await client.models.UserInboxItem.get({
      user_id: userId,
      chat_id: chatPk,
    });
    const getErrors = getResponse?.errors ?? [];
    if (getErrors.length) {
      throw new Error(getErrors.map((err) => err.message).join(", "));
    }

    const existing = getResponse?.data ?? null;
    if (!existing) return false;
    if (Number(existing?.unread_count || 0) <= 0) return false;

    const updateResponse = await client.models.UserInboxItem.update({
      user_id: userId,
      chat_id: chatPk,
      unread_count: 0,
      updated_at: viewedAtIso || new Date().toISOString(),
    });
    if (updateResponse?.errors?.length) {
      throw new Error(updateResponse.errors.map((err) => err.message).join(", "));
    }
    return true;
  } catch (error) {
    console.error("Failed to reset inbox projection unread count:", {
      userId,
      chatPk,
      error,
    });
    return false;
  }
}

export async function clearConversationUnreadProjection({
  userId,
  chatPk,
  viewedAtIso,
}) {
  const normalizedUserId = normalizeString(userId);
  const normalizedChatPk = normalizeString(chatPk);
  if (!normalizedUserId || !normalizedChatPk) return false;

  return resetInboxProjectionUnreadCount({
    userId: normalizedUserId,
    chatPk: normalizedChatPk,
    viewedAtIso,
  });
}

export function isConversationReadSupported() {
  return hasConversationReadModel();
}

export async function listConversationReadMarkers(userId) {
  const markers = new Map();
  const normalizedUserId = normalizeString(userId);
  if (!normalizedUserId || !hasConversationReadModel()) return markers;

  try {
    let nextToken = undefined;
    do {
      const response = await client.models.ConversationRead.list({
        filter: { user_id: { eq: normalizedUserId } },
        limit: 200,
        nextToken,
      });

      const { data, errors } = response ?? {};
      if (errors?.length) {
        throw new Error(errors.map((err) => err.message).join(", "));
      }

      for (const row of data || []) {
        const chatId = normalizeString(row?.chat_id);
        if (!chatId) continue;
        markers.set(chatId, {
          lastViewedMessageSk: normalizeString(row?.last_viewed_message_sk),
          lastViewedAt: row?.last_viewed_at || null,
        });
      }

      nextToken = response?.nextToken;
    } while (nextToken);
  } catch (error) {
    console.error("Failed to load conversation read markers:", error);
    return new Map();
  }

  return markers;
}

export async function getLatestIncomingMessageSk(chatPk, mySenderId) {
  const normalizedChatPk = normalizeString(chatPk);
  const normalizedSenderId = normalizeString(mySenderId);
  if (!normalizedChatPk || !normalizedSenderId) return "";

  let latestIncomingMessageSk = "";

  try {
    let nextToken = undefined;
    do {
      // Member-gated read via the listMyChatMessages custom query.
      const response = await client.queries.listMyChatMessages({
        chatPk: normalizedChatPk,
        limit: 200,
        nextToken,
      });

      const errors = Array.isArray(response?.errors) ? response.errors : [];
      if (errors.length) {
        throw new Error(errors.map((err) => err.message).join(", "));
      }

      const items = Array.isArray(response?.data?.items)
        ? response.data.items
        : [];
      for (const message of items) {
        const senderId = normalizeString(message?.message_from);
        if (!senderId || senderId === normalizedSenderId) continue;

        const messageSk = normalizeString(message?.sk);
        if (messageSk > latestIncomingMessageSk) {
          latestIncomingMessageSk = messageSk;
        }
      }

      nextToken = response?.data?.nextToken ?? null;
    } while (nextToken);
  } catch (error) {
    console.error("Failed to evaluate latest incoming message:", {
      chatPk: normalizedChatPk,
      error,
    });
    return "";
  }

  return latestIncomingMessageSk;
}

export function hasUnreadIncomingSince(lastViewedMessageSk, latestIncomingMessageSk) {
  const marker = normalizeString(lastViewedMessageSk);
  const latestIncoming = normalizeString(latestIncomingMessageSk);
  if (!latestIncoming) return false;
  if (!marker) return true;
  return latestIncoming > marker;
}

export function getLatestIncomingSkFromMessages(messages, mySenderId) {
  const normalizedSenderId = normalizeString(mySenderId);
  if (!normalizedSenderId || !Array.isArray(messages)) return "";

  let latestIncomingMessageSk = "";
  for (const message of messages) {
    const senderId = normalizeString(message?.senderId ?? message?.sender);
    if (!senderId || senderId === normalizedSenderId) continue;
    const messageSk = normalizeString(message?.timestamp ?? message?.sk);
    if (messageSk > latestIncomingMessageSk) {
      latestIncomingMessageSk = messageSk;
    }
  }

  return latestIncomingMessageSk;
}

export async function persistConversationReadMarker({
  userId,
  chatPk,
  lastViewedMessageSk,
  viewedAtIso,
}) {
  const normalizedUserId = normalizeString(userId);
  const normalizedChatPk = normalizeString(chatPk);
  const candidateSk = normalizeString(lastViewedMessageSk);
  if (!normalizedUserId || !normalizedChatPk || !candidateSk || !hasConversationReadModel()) {
    return false;
  }

  const markerInput = {
    user_id: normalizedUserId,
    chat_id: normalizedChatPk,
    last_viewed_message_sk: candidateSk,
    last_viewed_at: viewedAtIso || new Date().toISOString(),
  };

  try {
    const getResponse = await client.models.ConversationRead.get({
      user_id: normalizedUserId,
      chat_id: normalizedChatPk,
    });

    const getErrors = getResponse?.errors ?? [];
    if (getErrors.length) {
      throw new Error(getErrors.map((err) => err.message).join(", "));
    }

    const existing = getResponse?.data ?? null;
    const existingSk = normalizeString(existing?.last_viewed_message_sk);
    const nextSk = maxSk(existingSk, candidateSk);
    const markerViewedAt = markerInput.last_viewed_at;

    if (existing) {
      if (existingSk === nextSk) {
        return clearConversationUnreadProjection({
          userId: normalizedUserId,
          chatPk: normalizedChatPk,
          viewedAtIso: markerViewedAt,
        });
      }

      const updateResponse = await client.models.ConversationRead.update({
        user_id: normalizedUserId,
        chat_id: normalizedChatPk,
        last_viewed_message_sk: nextSk,
        last_viewed_at: markerViewedAt,
      });
      if (updateResponse?.errors?.length) {
        throw new Error(updateResponse.errors.map((err) => err.message).join(", "));
      }
      await clearConversationUnreadProjection({
        userId: normalizedUserId,
        chatPk: normalizedChatPk,
        viewedAtIso: markerViewedAt,
      });
      return true;
    }

    const createResponse = await client.models.ConversationRead.create(markerInput);
    if (createResponse?.errors?.length) {
      throw new Error(createResponse.errors.map((err) => err.message).join(", "));
    }
    await clearConversationUnreadProjection({
      userId: normalizedUserId,
      chatPk: normalizedChatPk,
      viewedAtIso: markerViewedAt,
    });
    return true;
  } catch (error) {
    console.error("Failed to persist conversation read marker:", {
      userId: normalizedUserId,
      chatPk: normalizedChatPk,
      error,
    });
    return false;
  }
}
